<script setup lang="ts">
import { onMounted } from 'vue';
import { usePlaylistStore } from '../stores/playlistStore';
import { fetchPlaylistDetail } from '../composables/useApi';

const playlistStore = usePlaylistStore();

onMounted(async () => {
  if (playlistStore.currentPlaylist) return; // already loaded
});

async function loadPlaylist(id: string) {
  playlistStore.loading = true;
  try {
    const detail = await fetchPlaylistDetail(id);
    playlistStore.openPlaylist(detail);
  } catch (e: unknown) {
    // handled by store
  } finally {
    playlistStore.loading = false;
  }
}

function formatSize(bytes?: number): string {
  if (!bytes) return '未知大小';
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

defineExpose({ loadPlaylist });
</script>

<template>
  <div v-if="playlistStore.currentPlaylist" class="playlist-detail">
    <div class="detail-header">
      <button class="back-btn" @click="playlistStore.goBack()">
        返回
      </button>
      <div class="header-info">
        <h2>{{ playlistStore.currentPlaylist.name }}</h2>
        <p v-if="playlistStore.currentPlaylist.description" class="desc">{{ playlistStore.currentPlaylist.description }}</p>
        <span class="total-episodes">共 {{ playlistStore.currentPlaylist.episodes.length }} 集</span>
      </div>
      <button class="play-all-btn" @click="playlistStore.playAllEpisodes()">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
        全部播放
      </button>
    </div>

    <div class="episode-list">
      <div
        v-for="(ep, i) in playlistStore.currentPlaylist.episodes"
        :key="ep.file_id"
        class="episode-item"
        :class="{ playing: playlistStore.playingEpisodeIndex === i }"
        @click="playlistStore.playEpisode(i)"
      >
        <div class="ep-number">
          <span class="num">{{ String(i + 1).padStart(2, '0') }}</span>
          <div class="playing-bar" v-if="playlistStore.playingEpisodeIndex === i">
            <span></span><span></span><span></span><span></span>
          </div>
        </div>
        <div class="ep-info">
          <div class="ep-name">{{ escapeHtml(ep.name) }}</div>
          <div class="ep-meta">
            <span>{{ formatSize(ep.size) }}</span>
            <span v-if="ep.segmented" class="hls-tag">HLS</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.playlist-detail {
  animation: fadeIn 0.3s ease-out;
  width: 100%;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  padding: 8px 14px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 4px;
}
.back-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.header-info {
  flex: 1;
}

.header-info h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
}

.header-info .desc {
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: 4px;
}

.total-episodes {
  color: var(--text-muted);
  font-size: 13px;
}

.play-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--accent-gradient);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all var(--transition-fast);
  flex-shrink: 0;
  margin-top: 4px;
}
.play-all-btn:hover {
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
  transform: translateY(-1px);
}

.episode-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.episode-item {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all var(--transition-normal);
  animation: fadeIn 0.3s ease-out both;
  width: 100%;
}
.episode-item:hover {
  border-color: var(--accent);
  background: var(--bg-elevated);
}
.episode-item.playing {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.ep-number {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
}

.ep-number .num {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-muted);
}

.episode-item.playing .ep-number .num {
  color: var(--accent);
}

.playing-bar {
  position: absolute;
  bottom: -2px;
  display: flex;
  gap: 2px;
  align-items: end;
  height: 12px;
}

.playing-bar span {
  width: 3px;
  background: var(--accent);
  border-radius: 1px;
  animation: playing 0.8s ease-in-out infinite alternate;
}
.playing-bar span:nth-child(1) { height: 60%; animation-delay: 0s; }
.playing-bar span:nth-child(2) { height: 100%; animation-delay: 0.2s; }
.playing-bar span:nth-child(3) { height: 40%; animation-delay: 0.4s; }
.playing-bar span:nth-child(4) { height: 80%; animation-delay: 0.6s; }

@keyframes playing {
  0% { transform: scaleY(0.3); }
  100% { transform: scaleY(1); }
}

.ep-info {
  flex: 1;
  min-width: 0;
}

.ep-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ep-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted);
  font-size: 12px;
}

.hls-tag {
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .playlist-detail {
    padding: 0 16px;
    max-width: 100%;
  }

  .detail-header {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  .back-btn {
    align-self: flex-start;
    margin-top: 0;
  }

  .header-info h2 {
    font-size: 20px;
  }

  .play-all-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 20px;
  }

  .episode-item {
    padding: 12px;
    gap: 10px;
  }

  .ep-number {
    width: 32px;
    height: 32px;
  }

  .ep-number .num {
    font-size: 12px;
  }

  .ep-name {
    font-size: 13px;
  }
}
</style>
